import React, { useState, useEffect } from "react";
import OpportunitiesFilters from "./OpportunitiesFilters";
import OpportunitiesFeed from "./OpportunitiesFeed";
import OpportunitiesRightSidebar from "./OpportunitiesRightSidebar";
import AddOpportunityModal from "./AddOpportunityModal";
import OpportunitiesPreferences from "./OpportunitiesPreferences";
import { Plus, Zap, Sparkles } from "lucide-react";
import { createOpportunity, getOpportunities, updateOpportunity, deleteOpportunity } from "../../services/opportunityService";
import { getMyApplications } from "../../services/applicationService";
import { useAuthContext } from "../../hooks/useAuthContext";

const OpportunitiesContent = ({ user, cvs, fetchCvs, onUploadCv, onDeleteCv, onSetDefaultCv }) => {
  const [filters, setFilters] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  const { token } = useAuthContext();
  const [refreshKey, setRefreshKey] = useState(0);
  const [allOpportunities, setAllOpportunities] = useState([]);
  const [rawApplications, setRawApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApplications = async () => {
      setLoading(true);
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const [opps, apps] = await Promise.all([
          getOpportunities(),
          getMyApplications(token)
        ]);
        setAllOpportunities(opps);
        setRawApplications(apps);
      } catch (err) {
        console.error("Failed to fetch applications", err);
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, [token, refreshKey]);

  const pastApplications = rawApplications.map(app => {
    const opp = allOpportunities.find(o => o.id === app.opportunityId);
    return {
      id: app.id,
      title: opp?.title || 'Unknown Opportunity',
      org: opp?.org || 'Unknown Org',
      date: new Date(app.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      status: app.status.charAt(0).toUpperCase() + app.status.slice(1)
    };
  });

  const savedIds = user?.saved_opportunity_ids || [];
  const savedOpportunities = allOpportunities.filter(opp => savedIds.includes(opp.id));

  const handleAddOpportunity = async (data) => {
    if (editingOpportunity) {
      await updateOpportunity(editingOpportunity.id, data, token);
      setEditingOpportunity(null);
    } else {
      await createOpportunity(data, token);
    }
    setRefreshKey(prev => prev + 1);
  };

  const handleDeleteOpportunity = async (id) => {
    if (window.confirm("Are you sure you want to delete this opportunity?")) {
      try {
        await deleteOpportunity(id, token);
        setRefreshKey(prev => prev + 1);
      } catch (err) {
        alert(err.message);
      }
    }
  };

  const handleEditOpportunity = (opp) => {
    setEditingOpportunity(opp);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingOpportunity(null);
  };

  return (
    <>
      <div className="max-w-[1440px] mx-auto px-6 py-6 min-h-full">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 mb-10">
          <div className="max-w-2xl">
            <h1 className="text-[28px] font-display font-bold text-foreground mb-1">Opportunities</h1>
            <p className="text-sm text-muted-foreground mt-1">Discover internships, volunteering, seminars, and job opportunities tailored for you.</p>
          </div>
          
          <div className="flex flex-wrap gap-4">
            {user?.role === 'admin' && (
              <button 
                onClick={() => setIsModalOpen(true)}
                className="group relative flex items-center gap-3 p-3 pr-5 rounded-xl bg-card border border-border hover:shadow-md transition-all duration-300 cursor-pointer text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-foreground group-hover:scale-105 transition-transform duration-300">
                  <Plus size={18} strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="text-sm font-bold font-display text-foreground leading-tight">Add Opportunity</h3>
                  <p className="text-xs font-medium text-muted-foreground mt-0.5">Post new content</p>
                </div>
              </button>
            )}

            <button className="group relative flex items-center gap-3 p-3 pr-5 rounded-xl bg-muted/50 border border-border hover:bg-muted transition-all duration-300 cursor-pointer text-left opacity-90 hover:opacity-100">
              <div className="w-10 h-10 rounded-lg bg-background/50 flex items-center justify-center text-foreground">
                <Zap size={18} className="text-secondary" fill="currentColor" strokeWidth={1} />
              </div>
              <div>
                <h3 className="text-sm font-bold font-display text-foreground leading-tight">Admin Analytics</h3>
                <p className="text-xs font-medium text-muted-foreground mt-0.5">Coming soon</p>
              </div>
            </button>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Left Sidebar: Filters + Weekly Digest */}
          <div className="hidden lg:block w-60 flex-shrink-0 space-y-6">
            <div className="sticky top-20 space-y-6">
              <OpportunitiesFilters filters={filters} onFiltersChange={setFilters} />
              <OpportunitiesPreferences />
              
              {/* Weekly Digest Card */}
              <div className="p-4 rounded-xl bg-[#9cbfa4] border border-[#8bae93] relative overflow-hidden group shadow-sm transition-all duration-300">
                <div className="relative z-10">
                  <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#0c3b31] mb-1.5">Weekly Digest</h3>
                  <p className="text-[11px] font-medium text-[#0c3b31]/80 leading-snug mb-4">Get personalised career tips and new matches every Monday.</p>
                  <button className="px-4 py-2 rounded-lg bg-[#0c3b31] text-white text-[10px] font-bold uppercase tracking-wide hover:opacity-90 active:scale-95 transition-all cursor-pointer border-none shadow-sm">
                    Subscribe
                  </button>
                </div>
                <div className="absolute -bottom-4 -right-4 opacity-20 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-500">
                   <Sparkles size={60} className="text-[#0c3b31]" fill="currentColor" strokeWidth={1} />
                </div>
              </div>
            </div>
          </div>

          {/* Main Content: Search + Feed */}
          <div className="flex-1 min-w-0">
            <OpportunitiesFeed 
              filters={filters} 
              user={user} 
              onEdit={handleEditOpportunity}
              onDelete={handleDeleteOpportunity}
              opportunities={allOpportunities}
              myApplications={rawApplications}
              setMyApplications={setRawApplications}
              loading={loading}
              userCvs={cvs}
              fetchUserCvs={fetchCvs}
            />
          </div>

          {/* Right Sidebar: Applications + Saved + CVs */}
          <div className="hidden xl:block w-72 flex-shrink-0">
             <div className="sticky top-20">
                <OpportunitiesRightSidebar 
                  pastApplications={pastApplications} 
                  savedOpportunities={savedOpportunities}
                  cvs={cvs} 
                  onDeleteCv={onDeleteCv} 
                  onSetDefaultCv={onSetDefaultCv} 
                  onUploadCv={onUploadCv}
                />
             </div>
          </div>
        </div>
      </div>

      <AddOpportunityModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAdd={handleAddOpportunity}
        editData={editingOpportunity}
        token={token}
      />
    </>
  );
};

export default OpportunitiesContent;
